import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import {
  destinationTypeOptions,
  materialCreateSchema,
  materialTypeOptions,
  type Material,
  type MaterialCreateInput,
} from './types';
import {
  useCreateMaterial,
  useProposeMaterialChange,
  useUpdateMaterialDirect,
} from './useMaterials';

export type MaterialsDialogMode = 'direct' | 'proposal';

interface MaterialsFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row?: Material | null;
  mode?: MaterialsDialogMode;
}

export const MaterialsFormDialog = ({
  open,
  onOpenChange,
  row,
  mode = 'direct',
}: MaterialsFormDialogProps) => {
  const { t } = useTranslation();
  const create = useCreateMaterial();
  const updateDirect = useUpdateMaterialDirect();
  const propose = useProposeMaterialChange();
  const isEdit = !!row;
  const isProposal = isEdit && mode === 'proposal';

  const formSchema = isProposal
    ? materialCreateSchema.extend({
        reason: z.string().min(1, t('efs.masters.form.reason_required', { ns: 'masters' })),
      })
    : materialCreateSchema.extend({ reason: z.string().optional() });

  type FormValues = MaterialCreateInput & { reason?: string };

  const defaults: FormValues = {
    japanese_name: '',
    normalized_name: '',
    material_type: 'metal',
    default_destination_type: 'buyer',
    price_reference_flag: false,
    hazardous_or_controlled_flag: false,
    is_high_value: false,
    esg_reporting_group: '',
    reason: '',
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    if (!open) {
      form.reset(defaults);
      return;
    }
    if (row) {
      form.reset({
        japanese_name: row.japanese_name,
        normalized_name: row.normalized_name,
        material_type: row.material_type,
        default_destination_type: row.default_destination_type,
        price_reference_flag: row.price_reference_flag,
        hazardous_or_controlled_flag: row.hazardous_or_controlled_flag,
        is_high_value: row.is_high_value,
        esg_reporting_group: row.esg_reporting_group ?? '',
        reason: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const { reason, ...payload } = values;
    if (isEdit && row) {
      if (isProposal) {
        await propose.mutateAsync({
          id: row.material_id,
          body: { proposed: payload, reason: reason ?? '' },
        });
      } else {
        await updateDirect.mutateAsync({ id: row.material_id, body: payload });
      }
    } else {
      await create.mutateAsync(payload);
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || updateDirect.isPending || propose.isPending;

  const title = !isEdit
    ? t('efs.masters.material.create', { ns: 'masters' })
    : isProposal
      ? t('efs.masters.form.mode_proposal', { ns: 'masters' })
      : t('efs.masters.form.mode_direct', { ns: 'masters' });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            {t('efs.masters.material.create_subtitle', {
              ns: 'masters',
              defaultValue: '素材登録は承認後に有効化されます',
            })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="japanese_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.masters.fields.japanese_name', { ns: 'masters' })}</FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="normalized_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.fields.normalized_name', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="material_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.masters.fields.material_type', { ns: 'masters' })}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {materialTypeOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {t(`efs.masters.material_type.${opt}`, {
                              ns: 'masters',
                              defaultValue: opt,
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="default_destination_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.fields.default_destination_type', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {destinationTypeOptions.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {t(`efs.masters.destination_type.${opt}`, {
                              ns: 'masters',
                              defaultValue: opt,
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="price_reference_flag"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="text-sm">
                      {t('efs.masters.fields.price_reference_flag', { ns: 'masters' })}
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="hazardous_or_controlled_flag"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="text-sm">
                      {t('efs.masters.fields.hazardous_or_controlled_flag', { ns: 'masters' })}
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_high_value"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    </FormControl>
                    <FormLabel className="text-sm">
                      {t('efs.masters.fields.is_high_value', {
                        ns: 'masters',
                        defaultValue: '高価材',
                      })}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="esg_reporting_group"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.fields.esg_reporting_group', { ns: 'masters' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isProposal ? (
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.masters.form.reason', { ns: 'masters' })}</FormLabel>
                    <FormControl>
                      <Textarea
                        rows={3}
                        placeholder={t('efs.masters.form.reason_placeholder', {
                          ns: 'masters',
                        })}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save')
                  : t('efs.masters.actions.create', { ns: 'masters' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
